
import React, { useState, useEffect } from "react";
import { apiUrl } from "../utils/api";
import { contactTypeLabel } from "../utils/contactTypeLabel";
import {
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogActions,
    Button,
    makeStyles,
    Input,
    Dropdown,
    Option,
    tokens,
    Text,
    Spinner,
    Table,
    TableHeader,
    TableRow,
    TableHeaderCell,
    TableBody,
    TableCell,
    Divider,
} from "@fluentui/react-components";
import {
    AddRegular,
    DeleteRegular,
    Search24Regular,
    ChevronRight20Regular,
    Person24Regular,
    ChevronDown24Regular,
    ChevronUp24Regular,
    BuildingMultiple24Regular
} from "@fluentui/react-icons";

const useStyles = makeStyles({
    surface: {
        width: "min(1080px, 95vw)",
        maxWidth: "1080px",
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        marginTop: "16px",
        overflowY: "auto",
        maxHeight: "60vh",
    },
    section: {
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        overflow: "hidden",
        marginBottom: "8px",
    },
    sectionHeader: {
        padding: "12px 16px",
        backgroundColor: tokens.colorNeutralBackground2,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontWeight: 600,
        userSelect: "none",
        "&:hover": {
            backgroundColor: tokens.colorNeutralBackground2Hover,
        }
    },
    sectionContent: {
        padding: "16px",
        backgroundColor: tokens.colorNeutralBackground1,
        display: "flex",
        flexDirection: "column",
        gap: "12px",
    },
    inputGroup: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr auto",
        gap: "12px",
        alignItems: "end",
    },
    field: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    inputGroupCompany: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr 80px auto",
        gap: "8px",
        alignItems: "end",
    },
    searchContainer: {
        marginBottom: "16px",
        padding: "16px",
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    searchResults: {
        maxHeight: "200px",
        overflowY: "auto",
        marginTop: "8px",
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow4,
    },
    searchResultItem: {
        padding: "8px 12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        "&:hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        "&:last-child": {
            borderBottom: "none",
        },
    },
    table: {
        marginTop: "24px",
    },
});

export type RelationshipItem = {
    id: number;
    type: 'Person' | 'Firma'; // Mapping to backend types or simplified "Private"/"Company"
    lastname: string;
    firstname?: string;
    role: string;
    function?: string;
    shares?: string;
    contactId?: number; // ID of existing contact if selected
};

interface RelationshipDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (items: RelationshipItem[]) => void;
}

export const RelationshipDialog: React.FC<RelationshipDialogProps> = ({
    open,
    onClose,
    onConfirm,
}) => {
    const s = useStyles();
    const [items, setItems] = useState<RelationshipItem[]>([]);

    // Collapsible states
    const [isPrivateOpen, setIsPrivateOpen] = useState(false);
    const [isCompanyOpen, setIsCompanyOpen] = useState(false);

    // Search State
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Private Input State
    const [pLastname, setPLastname] = useState("");
    const [pFirstname, setPFirstname] = useState("");
    const [pRole, setPRole] = useState("");

    // Company Input State
    const [cName, setCName] = useState("");
    const [cRole, setCRole] = useState("");
    const [cFunc, setCFunc] = useState("");
    const [cShares, setCShares] = useState("");

    // Selected Contact (temporary holding to auto-fill)
    const [selectedContact, setSelectedContact] = useState<any | null>(null);

    useEffect(() => {
        if (open) {
            setItems([]);
            resetPrivateInputs();
            resetCompanyInputs();
            setSearchQuery("");
            setSearchResults([]);
            setIsPrivateOpen(true); // Default open one
            setIsCompanyOpen(false);
        }
    }, [open]);

    const resetPrivateInputs = () => {
        setPLastname("");
        setPFirstname("");
        setPRole("");
        setSelectedContact(null);
    };

    const resetCompanyInputs = () => {
        setCName("");
        setCRole("");
        setCFunc("");
        setCShares("");
        setSelectedContact(null);
    };

    const handleSearch = async () => {
        if (!searchQuery) return;
        setIsSearching(true);
        try {
            const res = await fetch(apiUrl("contact", { search: searchQuery, limit: 50 }));
            const data = await res.json();
            setSearchResults(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to search contacts", err);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const selectContact = (contact: any) => {
        // Decide where to put it based on which section is open or contact type
        const isCompany = contact.type?.type === 'Firma'; // Rough check if data has type

        if (isCompany || isCompanyOpen) {
            // Auto fill company fields
            setCName(contact.lastname || contact.companyName || ""); // Companies often use lastname field or displayName
            setIsCompanyOpen(true);
            setIsPrivateOpen(false);
        } else {
            // Auto fill private fields
            setPLastname(contact.lastname || "");
            setPFirstname(contact.vorname || contact.firstName || "");
            setIsPrivateOpen(true);
            setIsCompanyOpen(false);
        }

        setSelectedContact(contact);
        setSearchQuery("");
        setSearchResults([]);
    };

    const handleAddPrivate = () => {
        if (!pLastname || !pRole) return;

        const newItem: RelationshipItem = {
            id: Date.now(),
            type: 'Person',
            lastname: pLastname,
            firstname: pFirstname,
            role: pRole,
            contactId: selectedContact?.id
        };

        setItems(prev => [...prev, newItem]);
        resetPrivateInputs();
    };

    const handleAddCompany = () => {
        if (!cName || !cRole) return;

        const newItem: RelationshipItem = {
            id: Date.now(),
            type: 'Firma',
            lastname: cName, // Using lastname to store company name for uniformity
            role: cRole,
            function: cFunc,
            shares: cShares,
            contactId: selectedContact?.id
        };

        setItems(prev => [...prev, newItem]);
        resetCompanyInputs();
    };

    const handleDelete = (id: number) => {
        setItems(prev => prev.filter(i => i.id !== id));
    };

    // Options
    const privateRoles = ["Ehemann", "Ehefrau", "Partner", "Kind", "Sonstiges"];
    const companyRoles = ["Tochterfirma", "Mutterfirma", "Beteiligung", "Sonstiges"]; // Example roles
    const companyFunctions = ["Gesellschafter", "Geschäftsführer", "Ansprechpartner"];

    return (
        <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
            <DialogSurface className={s.surface}>
                <DialogTitle>Verbindung hinzufügen</DialogTitle>
                <DialogBody className={s.content}>

                    {/* Search Section */}
                    <div className={s.searchContainer}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                            Bestehenden Kontakt suchen (optional)
                        </label>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Input
                                value={searchQuery}
                                onChange={(_, d) => setSearchQuery(d.value)}
                                placeholder="Kontakt suchen..."
                                contentAfter={
                                    isSearching ? <Spinner size="tiny" /> : <Search24Regular onClick={handleSearch} style={{ cursor: 'pointer' }} />
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSearch();
                                }}
                                style={{ flex: 1 }}
                            />
                            <Button onClick={handleSearch} disabled={isSearching}>Suchen</Button>
                        </div>
                        {searchResults.length > 0 && (
                            <div className={s.searchResults}>
                                {searchResults.map((result) => (
                                    <div
                                        key={result.id}
                                        className={s.searchResultItem}
                                        onClick={() => selectContact(result)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {result.type?.type === 'Firma' ? <BuildingMultiple24Regular /> : <Person24Regular />}
                                            <Text>
                                                {result.lastname} {result.firstName ? `, ${result.firstName}` : ""}
                                                {result.companyName && <span style={{ color: tokens.colorNeutralForeground3, fontSize: '12px', marginLeft: '8px' }}>({result.companyName})</span>}
                                            </Text>
                                        </div>
                                        <ChevronRight20Regular />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <Divider />

                    {/* Private Connection Section */}
                    <div className={s.section}>
                        <div
                            className={s.sectionHeader}
                            onClick={() => setIsPrivateOpen(!isPrivateOpen)}
                        >
                            <span>Privatverbindung hinzufügen</span>
                            {isPrivateOpen ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
                        </div>
                        {isPrivateOpen && (
                            <div className={s.sectionContent}>
                                <div className={s.inputGroup}>
                                    <div className={s.field}>
                                        <label>Rolle</label>
                                        <Dropdown
                                            value={pRole}
                                            onOptionSelect={(_, d) => setPRole(d.optionValue || "")}
                                            placeholder="Rolle wählen"
                                        >
                                            {privateRoles.map(r => <Option key={r} value={r}>{r}</Option>)}
                                        </Dropdown>
                                    </div>
                                    <div className={s.field}>
                                        <label>Nachname</label>
                                        <Input
                                            value={pLastname}
                                            onChange={(_, d) => setPLastname(d.value)}
                                            placeholder="Nachname"
                                        />
                                    </div>
                                    <div className={s.field}>
                                        <label>Vorname</label>
                                        <Input
                                            value={pFirstname}
                                            onChange={(_, d) => setPFirstname(d.value)}
                                            placeholder="Vorname"
                                        />
                                    </div>
                                    <Button
                                        icon={<AddRegular />}
                                        appearance="primary"
                                        onClick={handleAddPrivate}
                                        disabled={!pLastname || !pRole}
                                        style={{ marginBottom: '2px' }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Company Connection Section */}
                    <div className={s.section}>
                        <div
                            className={s.sectionHeader}
                            onClick={() => setIsCompanyOpen(!isCompanyOpen)}
                        >
                            <span>Firmenverbindung hinzufügen</span>
                            {isCompanyOpen ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
                        </div>
                        {isCompanyOpen && (
                            <div className={s.sectionContent}>
                                <div className={s.inputGroupCompany}>
                                    <div className={s.field}>
                                        <label>Firmenname</label>
                                        <Input
                                            value={cName}
                                            onChange={(_, d) => setCName(d.value)}
                                            placeholder="Firma GmbH"
                                        />
                                    </div>
                                    <div className={s.field}>
                                        <label>Rolle</label>
                                        <Dropdown
                                            value={cRole}
                                            onOptionSelect={(_, d) => setCRole(d.optionValue || "")}
                                            placeholder="Rolle wählen"
                                        >
                                            {companyRoles.map(r => <Option key={r} value={r}>{r}</Option>)}
                                        </Dropdown>
                                    </div>
                                    <div className={s.field}>
                                        <label>Funktion</label>
                                        <Dropdown
                                            value={cFunc}
                                            onOptionSelect={(_, d) => setCFunc(d.optionValue || "")}
                                            placeholder="Funktion wählen"
                                        >
                                            {companyFunctions.map(r => <Option key={r} value={r}>{r}</Option>)}
                                        </Dropdown>
                                    </div>
                                    <div className={s.field}>
                                        <label>Anteile</label>
                                        <Input
                                            value={cShares}
                                            onChange={(_, d) => setCShares(d.value)}
                                            placeholder="%"
                                            type="number"
                                        />
                                    </div>
                                    <Button
                                        icon={<AddRegular />}
                                        appearance="primary"
                                        onClick={handleAddCompany}
                                        disabled={!cName || !cRole}
                                        style={{ marginBottom: '2px' }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Table of new items */}
                    <div className={s.table}>
                        <Text size={400} weight="semibold" style={{ marginBottom: "8px", display: "block" }}>
                            Neue Verbindungen
                        </Text>
                        <Table aria-label="Neue Verbindungen">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>Name</TableHeaderCell>
                                    <TableHeaderCell>Typ</TableHeaderCell>
                                    <TableHeaderCell>Rolle</TableHeaderCell>
                                    <TableHeaderCell>Funktion/Anteile</TableHeaderCell>
                                    <TableHeaderCell />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            {item.lastname} {item.firstname ? `, ${item.firstname}` : ""}
                                        </TableCell>
                                        <TableCell>{contactTypeLabel(item.type === 'Person' ? 'Person' : 'Firma')}</TableCell>
                                        <TableCell>{item.role}</TableCell>
                                        <TableCell>
                                            {item.function ? item.function : ""}
                                            {item.shares ? ` (${item.shares}%)` : ""}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                icon={<DeleteRegular />}
                                                appearance="subtle"
                                                onClick={() => handleDelete(item.id)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {items.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} style={{ textAlign: "center", color: tokens.colorNeutralForeground4 }}>
                                            Bitte fügen Sie oben Verbindungen hinzu.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                </DialogBody>
                <DialogActions>
                    <Button onClick={onClose}>Abbrechen</Button>
                    <Button
                        appearance="primary"
                        onClick={() => onConfirm(items)}
                        disabled={items.length === 0}
                    >
                        Übernehmen
                    </Button>
                </DialogActions>
            </DialogSurface>
        </Dialog>
    );
};
